import {
  DATE_LIMIT_DEFAULT_END,
  DATE_LIMIT_DEFAULT_START,
  DATE_LIMIT_MAX_RANGE,
} from 'constants/DateLimits.constants';
import { isDefined } from 'shared/typechecks';
import {
  DateLimit,
  HubStudentConfiguration,
} from 'types/hubStudentSettings.type';
import { z } from 'zod';
import { EReservationStatuses } from '@timeedit/types/enums';
import { fieldSchema, mappedValueSchema } from '../../../validation';

const moduleSchema = z.object({
  name: mappedValueSchema.optional(),
  code: mappedValueSchema.optional(),
});

const datelimitSchema = z
  .object({
    absoluteStartTime: z.number().optional(),
    absoluteEndTime: z.number().optional(),
    relativeStartTime: z.number().optional(),
    relativeEndTime: z.number().optional(),
  })
  .superRefine(
    (
      {
        absoluteStartTime,
        absoluteEndTime,
        relativeStartTime,
        relativeEndTime,
      },
      ctx
    ) => {
      if (
        isDefined(absoluteStartTime) &&
        isDefined(absoluteEndTime) &&
        absoluteEndTime - absoluteStartTime > DATE_LIMIT_MAX_RANGE
      ) {
        ctx.issues.push({
          code: 'too_big',
          origin: 'number',
          maximum: DATE_LIMIT_MAX_RANGE,
          inclusive: true,
          input: absoluteEndTime - absoluteStartTime,
          path: ['absoluteEndTime'],
          message: 'Date range can be no longer than 1 year.',
        });
      }

      const relativeSpan =
        (relativeEndTime ?? DATE_LIMIT_DEFAULT_END) -
        (relativeStartTime ?? DATE_LIMIT_DEFAULT_START);
      if (relativeSpan > DATE_LIMIT_MAX_RANGE) {
        ctx.issues.push({
          code: 'too_big',
          origin: 'number',
          maximum: DATE_LIMIT_MAX_RANGE,
          inclusive: true,
          input: relativeSpan,
          path: ['relativeEndTime'],
          message: 'Offset can be no longer than 1 year.',
        });
      }
    }
  );

const locationSchema = z.object({
  room: mappedValueSchema.optional(),
  building: mappedValueSchema.optional(),
  campus: mappedValueSchema.optional(),
  type: mappedValueSchema.optional(),
  map: mappedValueSchema.optional(),
  image: mappedValueSchema.optional(),
  equipment: mappedValueSchema.optional(),
  onlineMeetingLink: mappedValueSchema.optional(),
  extraFields: z.array(fieldSchema).optional(),
});

const digitalResourcesSchema = z.object({
  recording: mappedValueSchema.optional(),
  lmsLink: mappedValueSchema.optional(),
});

const staffSchema = z.object({
  name: mappedValueSchema.optional(),
});

const activitySchema = z.object({
  displayName: mappedValueSchema.optional(),
  alternativeTitle: mappedValueSchema.optional(),
});

const hubStudentReservationSettingsSchema = z.object({
  reservationStatuses: z.array(z.nativeEnum(EReservationStatuses)).optional(),
  reservationMode: z.number().optional(),
  datelimit: datelimitSchema.optional(),
  module: moduleSchema.optional(),
  location: locationSchema.optional(),
  digitalResources: digitalResourcesSchema.optional(),
  staff: staffSchema.optional(),
  activity: activitySchema.optional(),
  messageToStudent: mappedValueSchema.optional(),
  extraFields: z.array(fieldSchema).optional(),
});

const hubStudentSubscribeSchema = z.object({
  enabled: z.boolean(),
  path: z.string().optional(),
  page: z.string().optional(),
});

const hubStudentSettingsSchema = z.object({
  studentReservationDisplaySettings:
    hubStudentReservationSettingsSchema.optional(),
  studentSubscribeSettings: hubStudentSubscribeSchema.optional(),
});

export const validateDateLimit = (datelimit: DateLimit) => {
  return datelimitSchema.safeParse(datelimit);
};

export const validateHubStudentSettings = (
  config: Partial<HubStudentConfiguration> | null
) => {
  return hubStudentSettingsSchema.safeParse(config);
};

export const validateHubStudentSettingsAsync = async (
  config: Partial<HubStudentConfiguration> | null
) => {
  return hubStudentSettingsSchema.safeParseAsync(config);
};
